import React, { memo, useMemo } from 'react';
import { INVENTORY_CHANGE_TYPE_META, InventoryParamChangeTypeBadge } from '../../../../globalUtils/globalExports';
import './chargerInventoryParameterChangeLogRowCard.css';

const NONE_LABEL = '(none)';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Formats a loggedAt into the two-line "Jun 25" / "at 10:02 AM" shape shown on the card. */
const formatChangeTimestamp = (loggedAt: string | number | Date): { date: string; time: string } => {
  const parsed = new Date(loggedAt);
  if (Number.isNaN(parsed.getTime())) return { date: '—', time: '' };

  const date = `${MONTHS[parsed.getMonth()]} ${parsed.getDate()}`;

  let hours = parsed.getHours();
  const minutes = parsed.getMinutes().toString().padStart(2, '0');
  const meridiem = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;

  return { date, time: `at ${hours}:${minutes} ${meridiem}` };
};

interface ChargerInventoryParameterChangeLogRowCardProps {
  item?: IInventoryParameterChangeLog;
  row?: IInventoryParameterChangeLog;
}

const ChargerInventoryParameterChangeLogRowCard = memo(
  function ChargerInventoryParameterChangeLogRowCard(
    props: ChargerInventoryParameterChangeLogRowCardProps,
  ) {
    const entry = props.item ?? props.row;

    const tags: string[] = [];
    
    if(entry?.connector != null) {
      tags.push(`CONNECTOR ${entry.connector}`);
    }
    
    if(entry?.smrUnitNo != null) {
      tags.push(`UNIT ${entry.smrUnitNo}`);
    }
    
    tags.push(entry?.parameter?.toUpperCase() ?? 'SERIAL')

    const meta = entry ? INVENTORY_CHANGE_TYPE_META[entry.changeType] : undefined;
    
    const { date, time } = useMemo(
      () => (entry ? formatChangeTimestamp(entry.loggedAt) : { date: '', time: '' }),
      [entry],
    );

    if (!entry || !meta) return null;

    const showNewValueChip = ['REPLACED', 'UPDATED', 'CARD REPLACED', 'UPGRADED', 'DOWNGRADED'].includes(entry.changeType);
    const isInvalid = entry.changeType === 'INVALID';

    return (
      <div
        className={`inventory__change__log__card inventory__change__log__card--${meta.tone}`}
        id={`inventory-change-log-card-${entry.id}`}
      >
        <span className='inventory__change__log__card__accent' />

        {/* Component + context chips */}
        <div className='inventory__change__log__card__component'>
          <span className='inventory__change__log__card__component__name' title={entry.component}>
            {entry.component}
          </span>
          <div className='inventory__change__log__card__tags'>
            {(tags).map((tag) => (
              <span className='inventory__change__log__card__tag' key={tag}>
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* old value → new value */}
        <div className='inventory__change__log__card__transition'>
          <span className='inventory__change__log__card__value inventory__change__log__card__value--old'>
            {entry.oldValue ?? NONE_LABEL}
          </span>

          <span className='inventory__change__log__card__arrow' aria-hidden='true'>
            →
          </span>

          {isInvalid ? (
            <span className='inventory__change__log__card__value inventory__change__log__card__value--invalid'>
              {'invalid bytes'}
            </span>
          ) : showNewValueChip ? (
            <span className='inventory__change__log__card__value inventory__change__log__card__value--new'>
              {entry.newValue ?? NONE_LABEL}
            </span>
          ) : (
            <span className='inventory__change__log__card__value inventory__change__log__card__value--plain'>
              {entry.newValue ?? NONE_LABEL}
            </span>
          )}
        </div>

        {/* status badge */}
        <div className='inventory__change__log__card__status'>
          <InventoryParamChangeTypeBadge changeType={entry.changeType} />
        </div>

        {/* loggedAt */}
        <div className='inventory__change__log__card__time'>
          <span className='inventory__change__log__card__time__date'>{date}</span>
          <span className='inventory__change__log__card__time__clock'>{time}</span>
        </div>
      </div>
    );
  },
);

export default ChargerInventoryParameterChangeLogRowCard;
